// Theme + brightness store — five themes (including a `system` option
// that follows prefers-color-scheme), persisted to localStorage and
// applied to <html> as a class.
//
// Architecture mirrors the FOUC-prevention <script> in index.html:
// same keys, same valid set, same brightness clamp. The inline script
// runs before React hydrates so the first paint already has the right
// palette + brightness; initTheme() below subscribes to OS preference
// changes for the "system" mode and re-syncs the in-memory cache so
// the Settings UI re-renders correctly.
//
// Theme is applied as `html.theme-<name>` (e.g. `html.theme-gunmetal`)
// — when the user picks "system", we resolve to `theme-warm-dark` or
// `theme-warm-light` based on the OS preference and update the class
// whenever that preference flips.

export type Theme =
  | "warm-light"
  | "warm-dark"
  | "gunmetal"
  | "slate"
  | "system";

// What "system" resolves to when applied. Anything else applies as-is.
export type ResolvedTheme = "warm-light" | "warm-dark" | "gunmetal" | "slate";

export const THEMES: Theme[] = [
  "warm-light",
  "warm-dark",
  "gunmetal",
  "slate",
  "system",
];

export const THEME_LABELS: Record<Theme, string> = {
  "warm-light": "Warm Light",
  "warm-dark": "Warm Dark",
  gunmetal: "Gunmetal",
  slate: "Slate",
  system: "System",
};

export const DEFAULT_THEME: Theme = "gunmetal";

export const BRIGHTNESS_MIN = 0.7;
export const BRIGHTNESS_MAX = 1.3;
export const BRIGHTNESS_STEP = 0.02;
export const DEFAULT_BRIGHTNESS = 1;

const THEME_KEY = "casethread_theme";
const BRIGHTNESS_KEY = "casethread_brightness";

function isTheme(v: string | null): v is Theme {
  return (
    v === "warm-light" ||
    v === "warm-dark" ||
    v === "gunmetal" ||
    v === "slate" ||
    v === "system"
  );
}

function clampBrightness(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_BRIGHTNESS;
  if (n < BRIGHTNESS_MIN) return BRIGHTNESS_MIN;
  if (n > BRIGHTNESS_MAX) return BRIGHTNESS_MAX;
  return n;
}

export function getTheme(): Theme {
  try {
    const raw = window.localStorage.getItem(THEME_KEY);
    return isTheme(raw) ? raw : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function getBrightness(): number {
  try {
    const raw = window.localStorage.getItem(BRIGHTNESS_KEY);
    if (raw == null) return DEFAULT_BRIGHTNESS;
    return clampBrightness(parseFloat(raw));
  } catch {
    return DEFAULT_BRIGHTNESS;
  }
}

function prefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveTheme(t: Theme): ResolvedTheme {
  if (t === "system") return prefersDark() ? "warm-dark" : "warm-light";
  return t;
}

const THEME_CLASSES: ResolvedTheme[] = [
  "warm-light",
  "warm-dark",
  "gunmetal",
  "slate",
];

function applyThemeClass(t: Theme): void {
  if (typeof document === "undefined") return;
  const resolved = resolveTheme(t);
  const html = document.documentElement;
  for (const cls of THEME_CLASSES) {
    html.classList.toggle(`theme-${cls}`, cls === resolved);
  }
}

function applyBrightness(n: number): void {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(
    "--brightness",
    String(clampBrightness(n)),
  );
}

type Listener = (state: { theme: Theme; brightness: number }) => void;
const listeners = new Set<Listener>();
let cachedTheme: Theme = DEFAULT_THEME;
let cachedBrightness: number = DEFAULT_BRIGHTNESS;
let mediaListenerInstalled = false;

function notify(): void {
  const snap = { theme: cachedTheme, brightness: cachedBrightness };
  for (const l of listeners) l(snap);
}

function ensureSystemListener(): void {
  if (mediaListenerInstalled) return;
  if (typeof window === "undefined" || !window.matchMedia) return;
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  // The handler only re-applies the class when the user is on
  // "system" — otherwise the OS preference is irrelevant and we'd be
  // doing wasted DOM writes.
  const handler = () => {
    if (cachedTheme === "system") {
      applyThemeClass("system");
      notify();
    }
  };
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", handler);
  } else if (typeof (mql as MediaQueryList).addListener === "function") {
    // Safari < 14 fallback. Cast keeps TS happy.
    (mql as MediaQueryList).addListener(handler);
  }
  mediaListenerInstalled = true;
}

export function setTheme(t: Theme): void {
  cachedTheme = t;
  try {
    window.localStorage.setItem(THEME_KEY, t);
  } catch {
    /* private mode — preference doesn't persist this session */
  }
  applyThemeClass(t);
  notify();
}

export function setBrightness(n: number): void {
  const clamped = clampBrightness(n);
  cachedBrightness = clamped;
  try {
    window.localStorage.setItem(BRIGHTNESS_KEY, String(clamped));
  } catch {
    /* ignore */
  }
  applyBrightness(clamped);
  notify();
}

export function subscribeTheme(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getThemeState(): { theme: Theme; brightness: number } {
  return { theme: cachedTheme, brightness: cachedBrightness };
}

// Called once from main.tsx before React renders. Idempotent with the
// FOUC script in index.html — re-applies whatever's in localStorage so
// the runtime cache and the DOM are in sync.
export function initTheme(): void {
  cachedTheme = getTheme();
  cachedBrightness = getBrightness();
  applyThemeClass(cachedTheme);
  applyBrightness(cachedBrightness);
  ensureSystemListener();
}
